/**
 * Browser-local cache of the AG Grid column layouts (order, width, visibility,
 * pinning, sort) that the grid wrappers save when a user rearranges columns.
 *
 * The server is the source of truth - see contextProviders/gridLayoutContext and
 * the `/gridLayouts` API. This cache exists only so a reload restores the columns
 * on first paint instead of flashing the default order while the request is in
 * flight. It is keyed per user, and the `localStorage.clear()` on sign out and
 * session expiry deliberately drops it, so nothing is left behind on a shared
 * browser; the layouts come back from the server on the next sign in.
 */

const GRID_LAYOUT_PREFIX = 'gridLayout:';

// PrivateRoute only renders a grid once the user is loaded, so this is really only
// reached in mock mode. Keeping a bucket for it means reads and writes still agree.
const NO_USER = 'anonymous';

const userPrefix = (userId)=> `${GRID_LAYOUT_PREFIX}${userId || NO_USER}:`;

const layoutKey = (userId, gridKey)=> `${userPrefix(userId)}${gridKey}`;

const isGridLayoutKey = (key)=> typeof key === 'string' && key.startsWith(GRID_LAYOUT_PREFIX);

const allKeys = ()=> {
  const keys = [];
  for(let i = 0; i < localStorage.length; i++) {
    keys.push(localStorage.key(i));
  }
  return keys;
};

const parse = (raw)=> {
  if(!raw) return null;
  try {
    return JSON.parse(raw);
  } catch(e) {
    return null;
  }
};

const readGridLayout = (userId, gridKey)=> {
  if(!gridKey) return null;

  try {
    return parse(localStorage.getItem(layoutKey(userId, gridKey)));
  } catch(e) {
    return null;
  }
};

/**
 * Every cached layout for one user, as `{[gridKey]: columnState}`. Lets the grids
 * paint from cache before the server's copy arrives.
 */
const readAllGridLayouts = (userId)=> {
  const layouts = {};

  try {
    const prefix = userPrefix(userId);
    allKeys().forEach((key)=> {
      if(!key || !key.startsWith(prefix)) return;

      const columnState = parse(localStorage.getItem(key));
      if(columnState) layouts[key.slice(prefix.length)] = columnState;
    });
  } catch(e) {
    return layouts;
  }

  return layouts;
};

const saveGridLayout = (userId, gridKey, columnState)=> {
  if(!gridKey || !columnState) return;

  try {
    localStorage.setItem(layoutKey(userId, gridKey), JSON.stringify(columnState));
  } catch(e) {}
};

const removeGridLayout = (userId, gridKey)=> {
  if(!gridKey) return;

  try {
    localStorage.removeItem(layoutKey(userId, gridKey));
  } catch(e) {}
};

export {
  readGridLayout,
  readAllGridLayouts,
  saveGridLayout,
  removeGridLayout,
  isGridLayoutKey,
  layoutKey,
  GRID_LAYOUT_PREFIX,
};
